import type { ShareProps, SharePlatform } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const STYLE = ["solid", "outline", "plain"] as const;
const SIZE = ["sm", "md", "lg"] as const;
const ALIGN = ["start", "center", "end"] as const;
const ALL_PLATFORMS: SharePlatform[] = [
  "twitter",
  "facebook",
  "linkedin",
  "whatsapp",
  "telegram",
  "reddit",
  "email",
];

export function ShareInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as ShareProps;
  const selected = new Set(p.platforms ?? []);

  const togglePlatform = (platform: SharePlatform) => {
    const next = new Set(selected);
    if (next.has(platform)) next.delete(platform);
    else next.add(platform);
    update(widget.id, {
      platforms: ALL_PLATFORMS.filter((pl) => next.has(pl)),
    });
  };

  return (
    <>
      <TextField
        label="URL to share (blank = current page)"
        value={p.url}
        onChange={(v) => update(widget.id, { url: v })}
      />
      <TextField
        label="Text / title"
        value={p.text}
        onChange={(v) => update(widget.id, { text: v })}
      />
      <SelectField
        label="Style"
        value={p.style ?? "outline"}
        options={STYLE}
        onChange={(v) => update(widget.id, { style: v as ShareProps["style"] })}
      />
      <SelectField
        label="Size"
        value={p.size ?? "md"}
        options={SIZE}
        onChange={(v) => update(widget.id, { size: v as ShareProps["size"] })}
      />
      <SelectField
        label="Align"
        value={p.align ?? "start"}
        options={ALIGN}
        onChange={(v) => update(widget.id, { align: v as ShareProps["align"] })}
      />
      <CheckboxField
        label="Show platform labels"
        value={p.showLabels ?? false}
        onChange={(v) => update(widget.id, { showLabels: v })}
      />
      <ColorField
        label="Colour"
        value={p.color ?? "#111827"}
        onChange={(v) => update(widget.id, { color: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Platforms
      </div>
      <div style={{ padding: "0 16px 12px", display: "grid", gap: 4 }}>
        {ALL_PLATFORMS.map((pl) => (
          <label
            key={pl}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={selected.has(pl)}
              onChange={() => togglePlatform(pl)}
            />
            {pl.charAt(0).toUpperCase() + pl.slice(1)}
          </label>
        ))}
      </div>
    </>
  );
}
